// Coverage-aware order card (POC): the per-row `coverage` object and the
// card's `coverage_summary` the backend stamps (coverage_card.service), and
// the pure helpers that turn them into a badge, a cost label and a total.
// Advisory only — nothing here changes what gets placed.

export type CoverageState = "covered" | "prior_auth" | "not_covered" | "out_of_network" | "unknown" | "checking";

export interface RowCoverage {
  state: CoverageState;
  cpt?: string;
  patient_cost_low?: number;
  patient_cost_high?: number;
  cost_basis?: "copay" | "deductible" | "coinsurance" | "not_covered" | "formulary_tier";
  cost_detail?: string;
  reason?: string;
  auth?: { policy_ref?: string; criteria_met?: string[]; criteria_missing?: string[]; packet_ready?: boolean };
  abn_eligible?: boolean;
  covering_dx?: { code: string; label: string; in_note: boolean };
  sources?: string[];
  checked_at?: string;
  confidence?: "high" | "medium" | "low";
}

export interface CoverageSummary {
  state: "verified" | "unverified" | "inactive" | "self_pay" | "no_insurance";
  payer_name?: string;
  plan_family?: string;
  plan_type?: string | null;
  plan_status?: string | null;
  office_copay?: number | null;
  ehr_eligibility?: string | null;
  secondary?: string[];
  deductible?: { total: number; met: number } | null;
  oop?: { total: number; met: number } | null;
  source?: string | null;
  reason?: string;
  checked_at?: string;
}

export const COVERAGE_BADGES: Record<CoverageState, { label: string; color: string; bg: string; border: string }> = {
  covered: { label: "Covered", color: "#166534", bg: "#DCFCE7", border: "#BBF7D0" },
  prior_auth: { label: "Prior auth", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  not_covered: { label: "Not covered", color: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  out_of_network: { label: "Out of network", color: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  unknown: { label: "Unknown", color: "#4B5563", bg: "#F3F4F6", border: "#E5E7EB" },
  checking: { label: "Checking…", color: "#4B5563", bg: "#F3F4F6", border: "#E5E7EB" },
};

const money = (v: number): string => {
  const rounded = Math.round(v);
  return `$${rounded.toLocaleString("en-US")}`;
};

// "~$0", "$35", "~$5–10", or "" when there is no estimate. A copay is exact;
// everything else is a range (or a tilde when the range collapses).
export const costLabel = (c: RowCoverage | undefined): string => {
  if (!c || c.patient_cost_low == null) return "";
  const lo = c.patient_cost_low;
  const hi = c.patient_cost_high ?? lo;
  if (c.cost_basis === "copay") return money(lo);
  if (Math.round(lo) === Math.round(hi)) return `~${money(lo)}`;
  return `~${money(lo)}–${money(hi).slice(1)}`;
};

// Sum of the row ranges for the rows the clinician has checked; null when
// no checked row carries an estimate.
export const selectedCostTotal = (
  orders: ReadonlyArray<{ coverage?: RowCoverage }>,
  selected: ReadonlySet<number>,
): { low: number; high: number } | null => {
  let low = 0;
  let high = 0;
  let seen = false;
  orders.forEach((o, i) => {
    const c = o.coverage;
    if (!selected.has(i) || !c || c.patient_cost_low == null) return;
    low += c.patient_cost_low;
    high += c.patient_cost_high ?? c.patient_cost_low;
    seen = true;
  });
  return seen ? { low, high } : null;
};

export const totalLabel = (t: { low: number; high: number } | null): string => {
  if (!t) return "";
  if (Math.round(t.low) === Math.round(t.high)) return `~${money(t.low)}`;
  return `~${money(t.low)}–${money(t.high).slice(1)}`;
};

// The one-line header strip: payer, plan, deductible progress. Empty parts
// are dropped so a thin summary still reads cleanly.
export const summaryParts = (s: CoverageSummary | null | undefined): string[] => {
  if (!s) return [];
  if (s.state === "self_pay") return ["Self-pay patient", "no coverage lookup"];
  if (s.state === "no_insurance") return ["No insurance on file"];
  const parts: string[] = [];
  const payer = [s.payer_name, s.plan_type].filter(Boolean).join(" · ");
  if (payer) parts.push(payer);
  if (s.state === "inactive") parts.push("marked inactive in the EHR");
  if (s.deductible && s.deductible.total > 0) {
    parts.push(`deductible ${money(s.deductible.met)} of ${money(s.deductible.total)} met`);
  }
  if (s.oop && s.oop.total > 0) parts.push(`out-of-pocket ${money(s.oop.met)} of ${money(s.oop.total)}`);
  if (s.office_copay != null && s.office_copay > 0) parts.push(`office copay ${money(s.office_copay)}`);
  if (s.secondary && s.secondary.length) parts.push(`secondary: ${s.secondary.join(", ")}`);
  return parts;
};

// Where the figures came from, for the small trailing caption.
export const summaryCaption = (s: CoverageSummary | null | undefined): string => {
  if (!s || s.state === "self_pay" || s.state === "no_insurance") return "";
  const bits: string[] = [];
  if (s.ehr_eligibility) bits.push(`EHR eligibility: ${s.ehr_eligibility.toLowerCase()}`);
  if (s.source === "mock_271") bits.push("benefits mocked for this preview");
  bits.push("estimates only");
  return bits.join(" · ");
};
